// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import { 
  Users, Search, ChevronLeft, ChevronRight, X,
  AlertCircle, Loader2, Calendar, AlertTriangle, Filter
} from 'lucide-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

// Status Badge
function StatusBadge({ status, isExpired }) {
  if (isExpired) {
    return (
      <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700">
        SIA Expired
      </span>
    );
  }
  
  const config = {
    ACTIVE: { bg: '#E8F5E9', text: '#2E7D32', label: 'Active' },
    SUSPENDED: { bg: '#FFEBEE', text: '#C62828', label: 'Suspended' },
    EXPIRED: { bg: '#FFF3E0', text: '#E65100', label: 'Expired' }
  };
  const style = config[status] || config.ACTIVE;
  
  return (
    <span 
      className="px-2 py-1 rounded text-xs font-semibold"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {style.label}
    </span>
  );
}

// Table Skeleton
function TableSkeleton() {
  return (
    <tbody className="divide-y divide-gray-100">
      {[...Array(8)].map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td className="px-6 py-4"><div className="h-4 w-32 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-28 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-5 w-16 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-8 w-28 bg-gray-200 rounded" /></td>
        </tr>
      ))}
    </tbody>
  );
}

// SIA Override Modal
function SIAOverrideModal({ officer, onClose, onSuccess }) {
  const [newExpiry, setNewExpiry] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newExpiry || !reason.trim()) {
      setError('New expiry date and reason are required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await api.patch(`/admin/officers/${officer.id}/override-sia`, { 
        new_expiry: newExpiry, 
        reason: reason.trim() 
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to override SIA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-xl shadow-2xl z-50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Override SIA Expiry</h2>
            <p className="text-sm text-gray-500">{officer.full_name}</p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">This action will:</p>
              <ul className="list-disc list-inside mt-1 text-amber-700">
                <li>Set a new SIA expiry date for this officer</li>
                <li>Reactivate the officer if currently expired</li>
                <li>Write OFFICER_SIA_OVERRIDDEN to audit log</li>
              </ul>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Expiry
            </label>
            <p className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              {officer.sia_expiry 
                ? new Date(officer.sia_expiry).toLocaleDateString('en-GB', {
                    day: '2-digit', month: 'long', year: 'numeric'
                  })
                : 'Not set'
              }
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Expiry Date *
            </label>
            <input
              type="date"
              value={newExpiry}
              onChange={(e) => setNewExpiry(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
              data-testid="sia-new-expiry"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason for Override *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 resize-none"
              rows={3}
              placeholder="Enter the reason for overriding the SIA expiry..."
              data-testid="sia-override-reason"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-10 rounded-lg font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: VCIN_COLORS.primary }}
              data-testid="confirm-sia-override-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              Override SIA
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default function SAOfficersPage() {
  const [officers, setOfficers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [overrideModal, setOverrideModal] = useState(null);

  const fetchOfficers = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 20 });
      if (companyFilter) params.append('company_id', companyFilter);
      
      const [officersRes, companiesRes] = await Promise.all([
        api.get(`/admin/officers?${params.toString()}`),
        api.get('/admin/companies?limit=100')
      ]);
      
      setOfficers(officersRes.data.officers);
      setPagination(officersRes.data.pagination);
      setCompanies(companiesRes.data.companies);
    } catch (err) {
      setError(err.message || 'Failed to load officers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOfficers();
  }, [companyFilter]);

  const filteredOfficers = officers.filter(officer =>
    officer.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    officer.email?.toLowerCase().includes(search.toLowerCase()) ||
    officer.sia_number?.toLowerCase().includes(search.toLowerCase())
  );

  if (error && !loading) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="sa-officers-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Officers</h1>
        <div className="text-sm text-gray-500">
          {pagination.total} officers across all companies
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or SIA number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
            data-testid="officer-search"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="h-10 px-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 min-w-48"
            data-testid="company-filter"
          >
            <option value="">All Companies</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="officers-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Full Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SIA Number</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SIA Expiry</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            
            {loading ? (
              <TableSkeleton />
            ) : filteredOfficers.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan="6" className="p-12 text-center">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium text-gray-600">No officers found</p>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="divide-y divide-gray-100">
                {filteredOfficers.map((officer) => (
                  <tr 
                    key={officer.id} 
                    className={`hover:bg-gray-50 transition-colors ${officer.is_expired ? 'bg-red-50/50' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-gray-900">{officer.full_name}</p>
                        <p className="text-xs text-gray-500">{officer.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {officer.company_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono text-sm text-gray-600">{officer.sia_number}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {officer.sia_expiry 
                        ? new Date(officer.sia_expiry).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })
                        : '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={officer.status} isExpired={officer.is_expired} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => setOverrideModal(officer)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
                        data-testid={`sia-override-${officer.id}`}
                      >
                        <Calendar className="w-3.5 h-3.5" />
                        Override SIA
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 20 + 1} to {Math.min(pagination.page * 20, pagination.total)} of {pagination.total}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchOfficers(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">Page {pagination.page} of {pagination.pages}</span>
              <button
                onClick={() => fetchOfficers(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* SIA Override Modal */}
      {overrideModal && (
        <SIAOverrideModal
          officer={overrideModal}
          onClose={() => setOverrideModal(null)}
          onSuccess={() => fetchOfficers(pagination.page)}
        />
      )}
    </div>
  );
}
